import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaDownload,
  FaExclamationTriangle,
  FaHistory,
  FaLayerGroup,
  FaMapMarkedAlt,
  FaSeedling,
  FaShieldAlt,
  FaTimesCircle,
  FaVectorSquare,
} from 'react-icons/fa';
import './MigrationReviewLocal.css';
import MigrationPerimeterEditor from './MigrationPerimeterEditor';

const REVIEW_DATA_URL = '/local-migration-review/review-data.json';
const DECISIONS_STORAGE_KEY = 'climorax:migration-review-decisions:v1';
const PERIMETERS_STORAGE_KEY = 'climorax:migration-review-perimeters:v1';

const formatDate = value => {
  if (!value) return 'Não informada';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const formatArea = value => {
  if (value === null || value === undefined || value === '') return 'Área não informada';
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(Number(value))} ha`;
};

const readSavedDecisions = () => {
  try {
    return JSON.parse(localStorage.getItem(DECISIONS_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const readSavedPerimeters = () => {
  try {
    return JSON.parse(localStorage.getItem(PERIMETERS_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

function StatusBadge({ status }) {
  const normalized = status || 'pendente_revisao';
  const labels = {
    preparado: 'Preparado',
    em_revisao: 'Em revisão',
    pendente_revisao: 'Revisão necessária',
    bloqueado: 'Bloqueado',
    rascunho: 'Rascunho',
  };
  return <span className={`migration-status is-${normalized}`}>{labels[normalized] || normalized}</span>;
}

function GeometryCanvas({ plots, selectedPlotId, onSelectPlot }) {
  const polygons = useMemo(() => plots
    .map((plot, index) => ({
      plot,
      index,
      coordinates: plot.geometria?.geometry?.type === 'Polygon'
        ? plot.geometria.geometry.coordinates?.[0] || []
        : [],
    }))
    .filter(item => item.coordinates.length >= 3), [plots]);

  if (polygons.length === 0) {
    return (
      <div className="migration-map-empty">
        <FaMapMarkedAlt aria-hidden="true" />
        <strong>Nenhuma geometria disponível</strong>
        <span>Os talhões continuam preservados e ficam pendentes até o perímetro ser revisado.</span>
      </div>
    );
  }

  const allPoints = polygons.flatMap(item => item.coordinates);
  const longitudes = allPoints.map(point => Number(point[0]));
  const latitudes = allPoints.map(point => Number(point[1]));
  const minX = Math.min(...longitudes);
  const maxX = Math.max(...longitudes);
  const minY = Math.min(...latitudes);
  const maxY = Math.max(...latitudes);
  const width = 760;
  const height = 410;
  const padding = 30;
  const rangeX = Math.max(maxX - minX, 0.000001);
  const rangeY = Math.max(maxY - minY, 0.000001);
  const projectPoint = point => {
    const x = padding + ((Number(point[0]) - minX) / rangeX) * (width - padding * 2);
    const y = height - padding - ((Number(point[1]) - minY) / rangeY) * (height - padding * 2);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  };

  return (
    <div className="migration-map-shell">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Comparação local dos perímetros dos talhões">
        <defs>
          <pattern id="migrationGrid" width="38" height="38" patternUnits="userSpaceOnUse">
            <path d="M 38 0 L 0 0 0 38" fill="none" stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width={width} height={height} rx="18" className="migration-map-background" />
        <rect width={width} height={height} rx="18" fill="url(#migrationGrid)" />
        {polygons.map(({ plot, coordinates, index }) => {
          const selected = String(plot.id) === String(selectedPlotId);
          return (
            <polygon
              key={plot.id}
              points={coordinates.map(projectPoint).join(' ')}
              className={`migration-map-polygon tone-${index % 5} ${selected ? 'is-selected' : ''}`}
              tabIndex="0"
              role="button"
              aria-label={`Selecionar ${plot.nome}`}
              onClick={() => onSelectPlot(plot.id)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') onSelectPlot(plot.id);
              }}
            />
          );
        })}
      </svg>
      <div className="migration-map-legend">
        <span><i /> Geometria copiada do talhão antigo</span>
        <small>Desenho comparativo sem mapa externo e sem conexão com produção.</small>
      </div>
    </div>
  );
}

function PlantingTimeline({ plantings }) {
  if (!plantings?.length) {
    return (
      <div className="migration-empty-history">
        <FaSeedling aria-hidden="true" />
        <span>Nenhum plantio foi inventado para esta área.</span>
      </div>
    );
  }

  return (
    <div className="migration-planting-timeline">
      {plantings.map(planting => (
        <article key={planting.id}>
          <div className="migration-timeline-dot" aria-hidden="true" />
          <div>
            <div className="migration-planting-heading">
              <strong>{planting.cultura || 'Cultura não informada'}</strong>
              <StatusBadge status={planting.mapeamentoStatus} />
            </div>
            <p>
              {formatDate(planting.dataPlantio)}
              <FaArrowRight aria-hidden="true" />
              {formatDate(planting.dataColheita || planting.dataColheitaPrevista)}
            </p>
            <small>Fonte preservada: {planting.fonteLegada} #{planting.fonteLegadaId}</small>
          </div>
        </article>
      ))}
    </div>
  );
}

function CandidateDecision({ candidate, propertyName, decision, onChange }) {
  const coverage = Number(candidate.cobertura || 0) * 100;
  return (
    <article className="migration-candidate-card">
      <div className="migration-candidate-topline">
        <div>
          <span>{propertyName}</span>
          <strong>{coverage.toFixed(1)}% de coincidência aproximada</strong>
        </div>
        <span className={`migration-candidate-strength is-${candidate.classificacao}`}>
          {candidate.classificacao === 'forte' ? 'Sinal forte' : candidate.classificacao === 'alta' ? 'Sinal alto' : 'Sinal moderado'}
        </span>
      </div>

      <div className="migration-candidate-pair">
        <div>
          <small>Talhão candidato A</small>
          <strong>{candidate.talhaoA.nome}</strong>
          <span>{candidate.talhaoA.cultura || 'Sem plantio'} · {formatDate(candidate.talhaoA.dataPlantio)}</span>
        </div>
        <FaLayerGroup aria-hidden="true" />
        <div>
          <small>Talhão candidato B</small>
          <strong>{candidate.talhaoB.nome}</strong>
          <span>{candidate.talhaoB.cultura || 'Sem plantio'} · {formatDate(candidate.talhaoB.dataPlantio)}</span>
        </div>
      </div>

      <div className="migration-decision-options" role="group" aria-label="Decisão sobre o possível agrupamento">
        {[
          { value: 'pendente', label: 'Deixar pendente', icon: FaClock },
          { value: 'agrupar', label: 'Mesma área física', icon: FaCheckCircle },
          { value: 'separar', label: 'Áreas diferentes', icon: FaTimesCircle },
        ].map(option => {
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              className={decision?.decisao === option.value ? 'is-selected' : ''}
              onClick={() => onChange({ ...decision, decisao: option.value })}
            >
              <Icon aria-hidden="true" />
              {option.label}
            </button>
          );
        })}
      </div>

      <label className="migration-decision-note">
        <span>Justificativa da revisão</span>
        <textarea
          rows="2"
          value={decision?.justificativa || ''}
          placeholder="Ex.: mesmo pivô em safras diferentes; confirmei o perímetro no mapa da fazenda."
          onChange={event => onChange({ ...decision, justificativa: event.target.value })}
        />
      </label>
    </article>
  );
}

export default function MigrationReviewLocal({ darkMode = false }) {
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('resumo');
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedPlotId, setSelectedPlotId] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyPending, setOnlyPending] = useState(false);
  const [decisions, setDecisions] = useState(readSavedDecisions);
  const [perimeters, setPerimeters] = useState(readSavedPerimeters);
  const [perimeterEditorTarget, setPerimeterEditorTarget] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`${REVIEW_DATA_URL}?v=${Date.now()}`, { cache: 'no-store' })
      .then(response => {
        if (!response.ok) throw new Error('O retrato local da homologação ainda não foi gerado.');
        return response.json();
      })
      .then(data => {
        if (!active) return;
        if (!data.somenteLeitura || data.producaoConectada) {
          throw new Error('O retrato local não passou na verificação de segurança.');
        }
        setReviewData(data);
        setSelectedPropertyId(data.propriedades?.[0]?.id || null);
      })
      .catch(fetchError => active && setError(fetchError.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    localStorage.setItem(DECISIONS_STORAGE_KEY, JSON.stringify(decisions));
  }, [decisions]);

  useEffect(() => {
    localStorage.setItem(PERIMETERS_STORAGE_KEY, JSON.stringify(perimeters));
  }, [perimeters]);

  const reviewProperties = useMemo(() => (reviewData?.propriedades || []).map(property => ({
    ...property,
    talhoes: property.talhoes.map(plot => {
      const localPerimeter = perimeters[String(plot.id)];
      return localPerimeter
        ? { ...plot, geometria: localPerimeter.geometria, area: localPerimeter.areaCalculada, perimetroLocal: true }
        : plot;
    }),
  })), [perimeters, reviewData]);

  const propertiesById = useMemo(() => new Map(
    reviewProperties.map(property => [property.id, property])
  ), [reviewProperties]);

  const filteredProperties = useMemo(() => reviewProperties.filter(property => {
    const matchesSearch = property.nome?.toLowerCase().includes(search.trim().toLowerCase());
    const hasPending = property.talhoes?.some(plot => plot.mapeamentoStatus === 'pendente_revisao');
    return matchesSearch && (!onlyPending || hasPending);
  }), [reviewProperties, search, onlyPending]);

  const selectedProperty = propertiesById.get(selectedPropertyId) || filteredProperties[0] || null;
  const selectedPlot = selectedProperty?.talhoes?.find(plot => String(plot.id) === String(selectedPlotId))
    || selectedProperty?.talhoes?.[0]
    || null;
  const decidedCandidates = Object.values(decisions).filter(item => item?.decisao && item.decisao !== 'pendente').length;

  const saveCandidateDecision = (candidateId, nextDecision) => {
    setDecisions(current => ({
      ...current,
      [candidateId]: {
        decisao: nextDecision?.decisao || 'pendente',
        justificativa: nextDecision?.justificativa || '',
        atualizadoEm: new Date().toISOString(),
      },
    }));
  };

  const exportDecisions = () => {
    const payload = {
      exportadoEm: new Date().toISOString(),
      origemGeradaEm: reviewData.geradoEm,
      ambiente: 'homologacao',
      alteraBanco: false,
      decisoesAgrupamento: reviewData.candidatosAgrupamento.map(candidate => ({
        candidatoId: candidate.id,
        propriedadeId: candidate.propriedadeId,
        talhaoALegadoId: candidate.talhaoA.legadoId,
        talhaoBLegadoId: candidate.talhaoB.legadoId,
        ...(decisions[candidate.id] || { decisao: 'pendente', justificativa: '' }),
      })),
      perimetrosCadastrados: Object.values(perimeters),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `revisao-migracao-talhoes-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <main className={`migration-review-local ${darkMode ? 'is-dark' : ''}`}><div className="migration-review-state">Carregando retrato da homologação…</div></main>;
  }

  if (error || !reviewData) {
    return (
      <main className={`migration-review-local ${darkMode ? 'is-dark' : ''}`}>
        <div className="migration-review-state is-error">
          <FaExclamationTriangle aria-hidden="true" />
          <strong>Não foi possível abrir a revisão</strong>
          <span>{error}</span>
        </div>
      </main>
    );
  }

  const summaryCards = [
    { label: 'Propriedades', value: reviewData.resumo.propriedades, icon: FaMapMarkedAlt, tone: 'blue' },
    { label: 'Talhões físicos', value: reviewData.resumo.talhoesFisicos, icon: FaVectorSquare, tone: 'green' },
    { label: 'Plantios históricos', value: reviewData.resumo.plantios, icon: FaSeedling, tone: 'teal' },
    { label: 'Sem geometria', value: reviewData.resumo.talhoesSemGeometria, icon: FaExclamationTriangle, tone: 'amber' },
    { label: 'Possíveis agrupamentos', value: reviewData.resumo.candidatosAgrupamento, icon: FaLayerGroup, tone: 'purple' },
  ];

  return (
    <main className={`migration-review-local ${darkMode ? 'is-dark' : ''}`}>
      <header className="migration-review-header">
        <div>
          <span className="migration-review-eyebrow">ClimoraX · ambiente isolado</span>
          <h1>Revisão da migração de talhões</h1>
          <p>Compare o modelo antigo com os novos talhões físicos e seus históricos de plantio.</p>
        </div>
        <button type="button" className="migration-export-button" onClick={exportDecisions}>
          <FaDownload aria-hidden="true" />
          Exportar decisões
        </button>
      </header>

      <section className="migration-safety-banner">
        <FaShieldAlt aria-hidden="true" />
        <div>
          <strong>Somente leitura · produção desconectada</strong>
          <span>As escolhas desta tela ficam apenas neste navegador e não alteram homologação ou produção.</span>
        </div>
        <span>{decidedCandidates}/{reviewData.candidatosAgrupamento.length} agrupamentos revisados</span>
      </section>

      <nav className="migration-section-tabs" aria-label="Seções da revisão">
        {[
          { key: 'resumo', label: 'Visão geral' },
          { key: 'propriedades', label: 'Talhões e plantios' },
          { key: 'agrupamentos', label: `Agrupamentos (${reviewData.resumo.candidatosAgrupamento})` },
          { key: 'pendencias', label: `Pendências (${reviewData.resumo.itensPendentes})` },
        ].map(item => (
          <button
            key={item.key}
            type="button"
            className={activeSection === item.key ? 'is-active' : ''}
            onClick={() => setActiveSection(item.key)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {activeSection === 'resumo' && (
        <section className="migration-overview">
          <div className="migration-summary-grid">
            {summaryCards.map(card => {
              const Icon = card.icon;
              return (
                <article key={card.label} className={`migration-summary-card tone-${card.tone}`}>
                  <Icon aria-hidden="true" />
                  <div><strong>{card.value}</strong><span>{card.label}</span></div>
                </article>
              );
            })}
          </div>

          <div className="migration-overview-grid">
            <article className="migration-overview-panel">
              <div className="migration-panel-heading">
                <div><span>Resultado técnico</span><h2>Reconciliação aprovada</h2></div>
                <FaCheckCircle aria-hidden="true" />
              </div>
              <ul className="migration-check-list">
                <li><FaCheckCircle /> {reviewData.resumo.talhoesLegados} talhões antigos preservados</li>
                <li><FaCheckCircle /> {reviewData.resumo.talhoesFisicos} talhões físicos rastreáveis</li>
                <li><FaCheckCircle /> {reviewData.resumo.plantios} plantios históricos ligados à fonte</li>
                <li><FaCheckCircle /> {reviewData.resumo.itensBloqueados} itens tecnicamente bloqueados</li>
                <li><FaCheckCircle /> {reviewData.resumo.talhoesAtivos} talhões publicados para clientes</li>
              </ul>
            </article>

            <article className="migration-overview-panel">
              <div className="migration-panel-heading">
                <div><span>Decisões humanas</span><h2>O que ainda precisa de você</h2></div>
                <FaClock aria-hidden="true" />
              </div>
              <button type="button" className="migration-overview-action" onClick={() => setActiveSection('agrupamentos')}>
                <span><strong>{reviewData.resumo.candidatosAgrupamento}</strong> possíveis áreas repetidas</span>
                <FaArrowRight />
              </button>
              <button type="button" className="migration-overview-action" onClick={() => setActiveSection('pendencias')}>
                <span><strong>{Object.keys(perimeters).length}/{reviewData.resumo.talhoesSemGeometria}</strong> perímetros desenhados localmente</span>
                <FaArrowRight />
              </button>
            </article>
          </div>
        </section>
      )}

      {activeSection === 'propriedades' && (
        <section className="migration-properties-layout">
          <aside className="migration-property-sidebar">
            <div className="migration-property-filters">
              <input
                type="search"
                value={search}
                placeholder="Buscar propriedade"
                aria-label="Buscar propriedade"
                onChange={event => setSearch(event.target.value)}
              />
              <label><input type="checkbox" checked={onlyPending} onChange={event => setOnlyPending(event.target.checked)} /> Somente com pendência</label>
            </div>
            <div className="migration-property-list">
              {filteredProperties.map(property => {
                const pending = property.talhoes.filter(plot => plot.mapeamentoStatus === 'pendente_revisao').length;
                return (
                  <button
                    key={property.id}
                    type="button"
                    className={property.id === selectedProperty?.id ? 'is-selected' : ''}
                    onClick={() => {
                      setSelectedPropertyId(property.id);
                      setSelectedPlotId(property.talhoes?.[0]?.id || null);
                    }}
                  >
                    <strong>{property.nome}</strong>
                    <span>{property.talhoes.length} talhões · {property.plantiosMapeados} plantios</span>
                    {pending > 0 && <small>{pending} sem geometria</small>}
                  </button>
                );
              })}
            </div>
          </aside>

          {selectedProperty && selectedPlot && (
            <div className="migration-property-detail">
              <div className="migration-property-title">
                <div><span>Propriedade selecionada</span><h2>{selectedProperty.nome}</h2></div>
                <StatusBadge status={selectedProperty.statusMigracao} />
              </div>

              <GeometryCanvas plots={selectedProperty.talhoes} selectedPlotId={selectedPlot.id} onSelectPlot={setSelectedPlotId} />

              <div className="migration-plot-selector">
                {selectedProperty.talhoes.map(plot => (
                  <button
                    key={plot.id}
                    type="button"
                    className={String(plot.id) === String(selectedPlot.id) ? 'is-selected' : ''}
                    onClick={() => setSelectedPlotId(plot.id)}
                  >
                    <FaVectorSquare aria-hidden="true" />
                    <span><strong>{plot.nome}</strong><small>{formatArea(plot.area)}</small></span>
                    {!plot.legado.possuiGeometria && <FaExclamationTriangle className="is-warning" aria-label="Sem geometria" />}
                  </button>
                ))}
              </div>

              <div className="migration-comparison-grid">
                <article className="migration-legacy-card">
                  <span>Origem antiga preservada</span>
                  <h3>{selectedPlot.legado.nome}</h3>
                  <dl>
                    <div><dt>ID antigo</dt><dd>#{selectedPlot.legado.id}</dd></div>
                    <div><dt>Cultura</dt><dd>{selectedPlot.legado.cultura || 'Sem plantio'}</dd></div>
                    <div><dt>Plantio</dt><dd>{formatDate(selectedPlot.legado.dataPlantio)}</dd></div>
                    <div><dt>Geometria</dt><dd>{selectedPlot.legado.possuiGeometria ? 'Preservada' : 'Pendente'}</dd></div>
                  </dl>
                </article>
                <article className="migration-new-card">
                  <span>Novo talhão físico</span>
                  <h3>{selectedPlot.nome}</h3>
                  <dl>
                    <div><dt>ID novo</dt><dd>#{selectedPlot.id}</dd></div>
                    <div><dt>Área</dt><dd>{formatArea(selectedPlot.area)}</dd></div>
                    <div><dt>Estratégia</dt><dd>Um para um seguro</dd></div>
                    <div><dt>Situação</dt><dd><StatusBadge status={selectedPlot.mapeamentoStatus} /></dd></div>
                  </dl>
                </article>
              </div>

              <section className="migration-history-panel">
                <div className="migration-panel-heading">
                  <div><span>Histórico do talhão físico</span><h2>Plantios migrados</h2></div>
                  <FaHistory aria-hidden="true" />
                </div>
                <PlantingTimeline plantings={selectedPlot.plantios} />
              </section>
            </div>
          )}
        </section>
      )}

      {activeSection === 'agrupamentos' && (
        <section className="migration-candidates-section">
          <div className="migration-section-intro">
            <span>Revisão territorial</span>
            <h2>Possíveis safras da mesma área física</h2>
            <p>A coincidência usa apenas a caixa externa dos polígonos. Confirme visualmente antes de marcar “Mesma área física”.</p>
          </div>
          {reviewData.candidatosAgrupamento.map(candidate => (
            <CandidateDecision
              key={candidate.id}
              candidate={candidate}
              propertyName={propertiesById.get(candidate.propriedadeId)?.nome || 'Propriedade'}
              decision={decisions[candidate.id] || { decisao: 'pendente', justificativa: '' }}
              onChange={nextDecision => saveCandidateDecision(candidate.id, nextDecision)}
            />
          ))}
        </section>
      )}

      {activeSection === 'pendencias' && (
        <section className="migration-pending-section">
          <div className="migration-section-intro">
            <span>Complementação necessária</span>
            <h2>Talhões sem geometria</h2>
            <p>Eles foram preservados e possuem destino físico, mas permanecem ocultos até o perímetro ser cadastrado ou confirmado.</p>
          </div>
          <div className="migration-pending-grid">
            {reviewProperties.flatMap(property => property.talhoes
              .filter(plot => plot.mapeamentoStatus === 'pendente_revisao')
              .map(plot => ({ property, plot })))
              .map(({ property, plot }) => (
                <article key={`${property.id}-${plot.id}`}>
                  <FaExclamationTriangle aria-hidden="true" />
                  <div>
                    <small>{property.nome}</small>
                    <strong>{plot.nome}</strong>
                    <span>{plot.plantios.length} plantio(s) preservado(s) · {formatArea(plot.area)}</span>
                    {plot.perimetroLocal && <em>Perímetro desenhado nesta revisão</em>}
                  </div>
                  <div className="migration-pending-actions">
                    <StatusBadge status={plot.perimetroLocal ? 'preparado' : plot.mapeamentoStatus} />
                    <button type="button" onClick={() => setPerimeterEditorTarget({ property, plot })}>
                      <FaMapMarkedAlt aria-hidden="true" />
                      {plot.perimetroLocal ? 'Editar perímetro' : 'Cadastrar perímetro'}
                    </button>
                  </div>
                </article>
              ))}
          </div>
        </section>
      )}

      {perimeterEditorTarget && (
        <MigrationPerimeterEditor
          property={perimeterEditorTarget.property}
          plot={perimeterEditorTarget.plot}
          existingDraft={perimeters[String(perimeterEditorTarget.plot.id)] || null}
          onClose={() => setPerimeterEditorTarget(null)}
          onSave={draft => {
            setPerimeters(current => ({ ...current, [String(draft.talhaoFisicoId)]: draft }));
            setPerimeterEditorTarget(null);
          }}
        />
      )}
    </main>
  );
}
